import React, { useState, useEffect } from 'react';
import { HiCheck, HiTrash, HiDownload } from 'react-icons/hi';
import { apsisService } from '../../services/apsisService';
import { supabase } from '../../supabaseClient';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import './styles/Modal.css';

function APSISManagement({ selectedEdition }) {
  const [settings, setSettings] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    if (selectedEdition) loadData();
  }, [selectedEdition]);

  const loadData = async () => {
    try {
      setLoading(true);
      const settingsData = await apsisService.getSettings(selectedEdition);
      const regsData = await apsisService.getRegistrations(selectedEdition);
      setSettings(settingsData);
      setRegistrations(regsData);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSettings = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  };

  const handleSaveSettings = async () => {
    try {
      await apsisService.updateSettings(settings);
      // Notify Header of visibility changes
      window.dispatchEvent(new CustomEvent('apsisSettingsUpdated'));
      setHasUnsavedChanges(false);
      showSuccess();
    } catch (e) {
      console.error(e);
      alert('Erreur lors de la sauvegarde.');
    }
  };

  const showSuccess = () => {
    setShowSaveSuccess(true);
    setTimeout(() => setShowSaveSuccess(false), 3000);
  };

  const handleFileUpload = async (e, field) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const path = `apsis/${selectedEdition}/${Date.now()}-${file.name}`;
        const { error } = await supabase.storage.from('speaker-images').upload(path, file);
        if (error) throw error;
        const { data: publicUrlData } = supabase.storage.from('speaker-images').getPublicUrl(path);
        handleUpdateSettings(field, publicUrlData.publicUrl);
      } catch (e) {
        console.error(e);
        alert('Erreur lors de l\'upload.');
      }
    }
  };

  const handleDeleteRegistration = async (id) => {
    if (window.confirm('Voulez-vous supprimer cette inscription ?')) {
      try {
        await apsisService.deleteRegistration(id);
        setRegistrations(prev => prev.filter(r => r.id !== id));
        showSuccess();
      } catch (e) {
        console.error(e);
      }
    }
  };

  const exportToCSV = () => {
    const headers = ['Société', 'Domaine', 'Email', 'Téléphone', 'Date'];
    const rows = registrations.map(r => [
      r.society,
      r.domaine,
      r.contact_email,
      r.phone_number,
      new Date(r.created_at).toLocaleString()
    ]);

    const csvContent = "data:text/csv;charset=utf-8," 
      + headers.join(",") + "\n"
      + rows.map(e => e.join(",")).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `apsis_registrations_${selectedEdition}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) return <div className="p-4">Chargement...</div>;

  return (
    <div className="admin-section">
      {showSaveSuccess && <div className="save-success-message"><HiCheck /> Succès !</div>}
      
      <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2>Gestion APSIS (Summit)</h2>
        {hasUnsavedChanges && (
          <button onClick={handleSaveSettings} className="btn-primary">
            Sauvegarder les modifications
          </button>
        )}
      </div>

      <div className="card" style={{ padding: '20px', marginBottom: '30px' }}>
        <h3>Paramètres de la section</h3>
        <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div className="form-group">
            <label>Titre de la section</label>
            <input 
              type="text" 
              value={settings.title} 
              onChange={e => handleUpdateSettings('title', e.target.value)} 
              placeholder="Ex: Agadir Private Security & Innovation Summit"
            />
          </div>
          <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '25px' }}>
             <input 
               type="checkbox" 
               id="is_visible"
               checked={settings.is_visible} 
               onChange={e => handleUpdateSettings('is_visible', e.target.checked)} 
             />
             <label htmlFor="is_visible">Afficher cette section sur le challenge</label>
          </div>
          <div className="form-group">
            <label>Image de présentation (Footer)</label>
            <input type="file" onChange={e => handleFileUpload(e, 'footer_image_url')} accept="image/*" />
            {settings.footer_image_url && (
              <img src={settings.footer_image_url} alt="preview" style={{ width: '100px', marginTop: '10px', borderRadius: '4px' }} />
            )}
          </div>
          <div className="form-group">
            <label>Document PDF (au clic sur l'image)</label>
            <input type="file" onChange={e => handleFileUpload(e, 'footer_pdf_url')} accept=".pdf" />
            {settings.footer_pdf_url && (
              <div style={{ marginTop: '10px' }}>
                <a href={settings.footer_pdf_url} target="_blank" rel="noreferrer">Voir le document actuel</a>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3>Inscriptions ({registrations.length})</h3>
        {registrations.length > 0 && (
          <Button 
            label="Exporter CSV" 
            icon={<HiDownload style={{ marginRight: '8px' }} />} 
            onClick={exportToCSV}
            className="p-button-outlined p-button-secondary"
          />
        )}
      </div>

      <div className="card">
        <DataTable 
          value={registrations} 
          className="p-datatable-sm"
          stripedRows
          showGridlines
          responsiveLayout="scroll"
          emptyMessage="Aucune inscription pour le moment."
        >
          <Column field="society" header="Société" sortable />
          <Column field="domaine" header="Domaine" sortable />
          <Column field="contact_email" header="Email" />
          <Column field="phone_number" header="Téléphone" />
          <Column 
            field="created_at" 
            header="Date d'inscription" 
            body={(rowData) => new Date(rowData.created_at).toLocaleString()}
            sortable 
          />
          <Column 
            header="Actions" 
            body={(rowData) => (
              <Button 
                icon={<HiTrash />} 
                onClick={() => handleDeleteRegistration(rowData.id)} 
                className="p-button-rounded p-button-danger p-button-text"
              />
            )}
            style={{ width: '80px' }}
          />
        </DataTable>
      </div>
    </div>
  );
}

export default APSISManagement;
